// src/components/proposal/PhaseReviewPanel.tsx
// Review/approve panel for ONE generation gate in the checkpoint-gated flow.
// Shows what the phase produced (editable sub-sections), what it feeds, any AI
// clarifying questions (critical ones block Approve), and Approve / Regenerate.

import React, { useMemo, useState } from 'react';
import {
  Check,
  RefreshCw,
  AlertTriangle,
  HelpCircle,
  ArrowRight,
  Loader2,
  Info,
  PauseCircle,
  Trash2,
  Sparkles,
  Pencil,
  Eye,
  MessageSquarePlus,
  X,
} from 'lucide-react';
import {
  GenerationPhaseId,
  GenerationPhaseState,
  GenerationQuestion,
  GENERATION_PHASE_META,
  DeliverablesData,
} from '../../services/orchestrated-proposal-service';
import ProposalSectionRenderer from './ProposalSectionRenderer';
import StructuredSectionEditor from './StructuredSectionEditor';
import { extractDataNeeded } from '../../lib/section-content-utils';

interface PhaseReviewPanelProps {
  phaseId: GenerationPhaseId;
  phaseState: GenerationPhaseState;
  questions: GenerationQuestion[];
  busy: boolean;
  // True when this gate shows `generating` but nothing is actively running it
  // (e.g. the browser left mid-generation) — render the recoverable state.
  interrupted: boolean;
  isLastPhase: boolean;
  onApprove: () => void;
  onRegenerate: () => void;
  // One explicit retry of THIS gate from the last good state (no auto-loop).
  onResume: () => void;
  // Abandon the whole session.
  onAbandon: () => void;
  onAnswer: (questionId: string, answer: string) => void;
  onEdit: (key: string, value: string) => void;
  // Open the free-text feedback dialog to refine THIS gate (records a global
  // correction, then regenerates just this gate with it applied).
  onRefineWithFeedback: () => void;
  // Global corrections applied to every section, with a remover.
  corrections: UserCorrection[];
  onRemoveCorrection: (id: string) => void;
}

export interface UserCorrection {
  id: string;
  phase: string;
  text: string;
  at?: string;
}

interface EditableSection {
  key: string;
  title: string;
  content: string;
  editable: boolean;
}

// Backbone is structured data (deliverables/labor/cost) — summarize it read-only.
function backboneSummary(payload: DeliverablesData | null): string {
  if (!payload) return 'No deliverables data was produced.';
  const count = payload.deliverables_with_labor?.length || 0;
  const hours = payload.labor_summary?.total_hours?.recommended || 0;
  const odc = (payload as any)?.non_labor_summary?.total_estimated_cost || 0;
  const total = payload.ai_pricing?.cost_rollup?.total_price || 0;
  const cats = payload.labor_summary?.by_category?.length || 0;
  return [
    `${count} deliverable${count === 1 ? '' : 's'} mapped`,
    `${cats} labor categor${cats === 1 ? 'y' : 'ies'}`,
    `${hours.toLocaleString()} recommended hours`,
    odc ? `$${Math.round(odc).toLocaleString()} non-labor (ODC)` : null,
    total ? `~$${Math.round(total).toLocaleString()} indicative total` : null,
  ].filter(Boolean).join(' · ');
}

export function getEditableSections(phaseId: GenerationPhaseId, phaseState: GenerationPhaseState): EditableSection[] {
  const payload = phaseState.payload || {};
  const edits = phaseState.edits || {};
  const val = (key: string, section: any) =>
    edits[key] ?? (section?.content || '');

  switch (phaseId) {
    case 'backbone':
      return [{
        key: 'deliverables',
        title: 'Deliverables, labor & cost backbone',
        content: backboneSummary(payload),
        editable: false,
      }];
    case 'technical-management':
      return [
        { key: 'technical', title: 'Technical Approach', content: val('technical', payload.technical), editable: true },
        { key: 'management', title: 'Management Approach', content: val('management', payload.management), editable: true },
      ];
    case 'pp-personnel':
      return [
        { key: 'pastPerformance', title: 'Past Performance', content: val('pastPerformance', payload.pastPerformance), editable: true },
        { key: 'personnel', title: 'Key Personnel', content: val('personnel', payload.personnel), editable: true },
      ];
    case 'pricing-compliance':
      return [
        { key: 'pricing', title: 'Pricing', content: val('pricing', payload.pricing), editable: true },
        { key: 'compliance', title: 'FAR/DFARS Compliance', content: val('compliance', payload.compliance), editable: true },
      ];
    case 'summary-cover':
      return [
        { key: 'executiveSummary', title: 'Executive Summary', content: val('executiveSummary', payload.executiveSummary), editable: true },
        { key: 'coverLetter', title: 'Cover Letter', content: val('coverLetter', payload.coverLetter), editable: true },
      ];
    default:
      return [];
  }
}

export const PhaseReviewPanel: React.FC<PhaseReviewPanelProps> = ({
  phaseId,
  phaseState,
  questions,
  busy,
  interrupted,
  isLastPhase,
  onApprove,
  onRegenerate,
  onResume,
  onAbandon,
  onAnswer,
  onEdit,
  onRefineWithFeedback,
  corrections,
  onRemoveCorrection,
}) => {
  const meta = GENERATION_PHASE_META[phaseId];
  const sections = useMemo(() => getEditableSections(phaseId, phaseState), [phaseId, phaseState]);
  // Which editable sub-sections are currently in raw-edit mode (default is the
  // formatted, human-readable preview — not raw JSON).
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const phaseQuestions = questions.filter(q => q.phase === phaseId);
  const unansweredCritical = phaseQuestions.some(q => q.severity === 'critical' && !q.answer);
  const regenerateRequired = !!phaseState.regenerateRequired;
  const isApproved = phaseState.status === 'approved' || phaseState.approved;

  // Recoverable stop: a recovery card with Resume / Abandon, shown both when the
  // gate errored and when it was interrupted mid-generation. Nothing auto-retries;
  // the user explicitly chooses to resume from the last good state or abandon.
  const renderRecoverable = (opts: { title: string; detail: string; icon: 'error' | 'interrupted' }) => (
    <div className="text-center py-12">
      <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${opts.icon === 'error' ? 'bg-urgent-bg' : 'bg-fit-moderate-bg'}`}>
        {opts.icon === 'error'
          ? <AlertTriangle className="w-7 h-7 text-urgent" />
          : <PauseCircle className="w-7 h-7 text-fit-moderate" />}
      </div>
      <h3 className="text-lg font-semibold text-bw-ink mb-1">{opts.title}</h3>
      <p className="text-bw-muted mb-1 max-w-md mx-auto text-sm">{opts.detail}</p>
      <p className="text-bw-muted mb-6 max-w-md mx-auto text-xs">
        Nothing runs again on its own. Resume re-runs just this step from your last approved
        work, or abandon to discard this generation.
      </p>
      <div className="flex justify-center gap-3">
        <button
          onClick={onAbandon}
          disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2 text-bw-ink bg-bw-surface border border-bw-border rounded-lg hover:bg-bw-surface-subtle transition-colors text-sm font-medium disabled:opacity-50"
        >
          <Trash2 className="w-4 h-4" />
          Abandon
        </button>
        <button
          onClick={onResume}
          disabled={busy}
          className="inline-flex items-center gap-2 px-5 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
          Resume from last state
        </button>
      </div>
    </div>
  );

  // Actively generating right now.
  if (phaseState.status === 'generating' && !interrupted) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="h-8 w-8 text-bw-primary animate-spin" />
        <p className="text-bw-ink mt-4 font-medium">Generating {meta.label}…</p>
        <p className="text-sm text-bw-muted mt-1">This step compounds on your approved prior work.</p>
      </div>
    );
  }

  // Interrupted: stuck in `generating` with nothing running it.
  if (phaseState.status === 'generating' && interrupted) {
    return renderRecoverable({
      title: `${meta.label} was interrupted`,
      detail: 'This step was still generating when you left, so it never finished.',
      icon: 'interrupted',
    });
  }

  if (phaseState.status === 'error') {
    return renderRecoverable({
      title: `${meta.label} failed`,
      detail: phaseState.error || 'The generation step did not complete.',
      icon: 'error',
    });
  }

  // Not yet generated (e.g. resumed onto a gate whose generation never started).
  // Explicit, user-initiated start — nothing runs on its own.
  if (phaseState.status === 'not_started') {
    return (
      <div className="text-center py-12">
        <div className="w-14 h-14 bg-bw-primary-tint rounded-full flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-7 h-7 text-bw-primary" />
        </div>
        <h3 className="text-lg font-semibold text-bw-ink mb-1">Ready to generate {meta.label}</h3>
        <p className="text-bw-muted mb-6 max-w-md mx-auto text-sm">
          This step compounds on your approved prior work. Start it when you're ready.
        </p>
        <button
          onClick={onResume}
          disabled={busy}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Generate this step
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* What this phase is + what it feeds */}
      <div className="bg-bw-surface rounded-xl border shadow-sm p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-bw-ink">{meta.label}</h3>
              {isApproved && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-success bg-success-bg border border-success-border rounded-full px-2 py-0.5">
                  <Check className="w-3 h-3" /> Approved
                </span>
              )}
            </div>
            <p className="text-xs text-bw-muted mt-0.5">
              Produces: {meta.produces.join(', ')}
            </p>
          </div>
          {meta.feeds.length > 0 && (
            <p className="text-xs text-bw-muted inline-flex items-center gap-1.5 bg-bw-surface-subtle border border-bw-border rounded-full px-2.5 py-1">
              <ArrowRight className="w-3.5 h-3.5 text-slate-500" />
              Feeds: {meta.feeds.map(f => GENERATION_PHASE_META[f].label).join(', ')}
            </p>
          )}
        </div>
        {isApproved && (
          <p className="text-xs text-bw-muted mt-2">
            Already approved. Regenerating this step will reset every step that depends on it.
          </p>
        )}
      </div>

      {/* Answers pending: this phase must be regenerated for them to take effect */}
      {regenerateRequired && (
        <div className="rounded-xl border border-amber-200 bg-fit-moderate-bg p-3 flex items-start gap-2">
          <RefreshCw className="w-4 h-4 text-fit-moderate flex-shrink-0 mt-0.5" />
          <p className="text-sm text-fit-moderate">
            You answered a question after this step was generated. <span className="font-medium">Regenerate</span> to
            fold your answers in — approval is blocked until then.
          </p>
        </div>
      )}

      {/* AI clarifying questions */}
      {phaseQuestions.length > 0 && (
        <div className="bg-bw-surface rounded-xl border shadow-sm p-4 space-y-3">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-fit-moderate" />
            <span className="font-medium text-bw-ink text-sm">The AI needs your input</span>
            {unansweredCritical && (
              <span className="text-xs font-medium text-urgent bg-urgent-bg border border-urgent-border rounded-full px-2 py-0.5">
                Critical questions block approval
              </span>
            )}
          </div>
          {phaseQuestions.map(q => (
            <div
              key={q.id}
              className={`rounded-lg border p-3 ${
                q.severity === 'critical' ? 'border-red-200 bg-urgent-bg/40' : 'border-bw-border bg-bw-surface-subtle'
              }`}
            >
              <div className="flex items-start gap-2">
                <span className={`mt-0.5 text-xs font-semibold px-1.5 py-0.5 rounded ${
                  q.severity === 'critical' ? 'bg-urgent-bg text-urgent' : 'bg-gray-200 text-bw-muted'
                }`}>
                  {q.severity === 'critical' ? 'Critical' : 'Optional'}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-bw-ink">{q.question}</p>
                  {q.whyItMatters && (
                    <p className="text-xs text-bw-muted mt-0.5">{q.whyItMatters}</p>
                  )}
                  {q.currentAssumption && (
                    <p className="text-xs text-bw-muted mt-0.5">
                      Current assumption: {q.currentAssumption}
                    </p>
                  )}
                  {q.options && q.options.length > 0 ? (
                    <select
                      value={q.answer || ''}
                      onChange={e => onAnswer(q.id, e.target.value)}
                      className="mt-2 w-full px-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    >
                      <option value="">Select…</option>
                      {q.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      defaultValue={q.answer || ''}
                      onBlur={e => onAnswer(q.id, e.target.value)}
                      placeholder="Your answer…"
                      className="mt-2 w-full px-3 py-2 border border-bw-border rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
          <p className="text-xs text-bw-muted">
            After answering, use <span className="font-medium">Regenerate</span> to fold your answers into this phase.
          </p>
        </div>
      )}

      {/* Global corrections applied to every section */}
      {corrections.length > 0 && (
        <div className="bg-bw-surface rounded-xl border shadow-sm p-4 space-y-2">
          <div className="flex items-center gap-2">
            <MessageSquarePlus className="w-4 h-4 text-slate-600" />
            <span className="font-medium text-bw-ink text-sm">Corrections applied to all sections</span>
          </div>
          <p className="text-xs text-bw-muted">
            These override the AI's own read of the solicitation and compound into this step and
            every step after it.
          </p>
          <ul className="space-y-1.5">
            {corrections.map(c => (
              <li key={c.id} className="flex items-start gap-2 rounded-lg border border-bw-border bg-bw-surface-subtle px-3 py-2">
                <span className="text-xs font-medium text-slate-500 mt-0.5 whitespace-nowrap">
                  {GENERATION_PHASE_META[c.phase as GenerationPhaseId]?.label || c.phase}
                </span>
                <p className="text-sm text-bw-ink flex-1 min-w-0">{c.text}</p>
                <button
                  type="button"
                  onClick={() => onRemoveCorrection(c.id)}
                  disabled={busy}
                  title="Remove this correction"
                  className="text-bw-muted hover:text-urgent transition-colors disabled:opacity-50"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Produced sub-sections */}
      <div className="space-y-4">
        {sections.map(section => {
          const isEditing = !!editing[section.key];
          const dataNeeded = section.editable ? extractDataNeeded(section.content) : [];
          return (
            <div key={section.key} className="bg-bw-surface rounded-xl border shadow-sm p-4 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-bw-ink text-sm">{section.title}</h4>
                {section.editable ? (
                  <button
                    type="button"
                    onClick={() => setEditing(prev => ({ ...prev, [section.key]: !prev[section.key] }))}
                    className="text-xs text-bw-muted hover:text-bw-ink inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-bw-surface-subtle transition-colors"
                  >
                    {isEditing ? <><Eye className="w-3.5 h-3.5" /> Preview</> : <><Pencil className="w-3.5 h-3.5" /> Edit</>}
                  </button>
                ) : (
                  <span className="text-xs text-bw-muted inline-flex items-center gap-1">
                    <Info className="w-3.5 h-3.5" /> summary
                  </span>
                )}
              </div>

              {/* Yellow callout: everything the AI flagged as needing your data */}
              {dataNeeded.length > 0 && (
                <div className="mb-3 rounded-lg border border-amber-300 bg-fit-moderate-bg p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <AlertTriangle className="w-4 h-4 text-fit-moderate" />
                    <span className="text-sm font-semibold text-fit-moderate">
                      {dataNeeded.length} item{dataNeeded.length === 1 ? '' : 's'} need your data
                    </span>
                  </div>
                  <ul className="space-y-1">
                    {dataNeeded.map((d, i) => (
                      <li key={i} className="text-xs text-amber-900 flex items-start gap-1.5">
                        <span className="font-medium whitespace-nowrap text-fit-moderate">{d.field}:</span>
                        <span className="bg-amber-200/60 rounded px-1 py-0.5">{d.marker}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-fit-moderate mt-1.5">
                    Fill these in via <span className="font-medium">Edit</span>, or use <span className="font-medium">Refine with feedback</span> to have the AI address them.
                  </p>
                </div>
              )}

              {!section.editable ? (
                <p className="text-sm text-bw-ink">{section.content}</p>
              ) : isEditing ? (
                <StructuredSectionEditor
                  content={section.content}
                  onChange={value => onEdit(section.key, value)}
                />
              ) : section.content ? (
                <div className="max-h-[460px] overflow-auto rounded-lg border border-bw-border bg-bw-surface-subtle/40 px-4 py-3 break-words">
                  <ProposalSectionRenderer
                    sectionType={section.key}
                    content={section.content}
                    showCitations={false}
                  />
                </div>
              ) : (
                <p className="text-sm text-bw-muted italic">No content was produced for this section.</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-2">
          <button
            onClick={onRegenerate}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 text-bw-ink bg-bw-surface-subtle border border-bw-border rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium disabled:opacity-50"
            title="Re-runs this whole step (both sub-sections) and clears downstream phases"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
            Regenerate
          </button>
          <button
            onClick={onRefineWithFeedback}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 text-slate-700 bg-bw-surface border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium disabled:opacity-50"
            title="Tell the AI what to fix; the correction is recorded globally and this step is regenerated with it applied"
          >
            <MessageSquarePlus className="w-4 h-4" />
            Refine with feedback
          </button>
        </div>

        {isApproved ? (
          <span className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-success">
            <Check className="w-4 h-4" /> Approved
          </span>
        ) : (
          <button
            onClick={onApprove}
            disabled={busy || unansweredCritical || regenerateRequired || phaseState.status !== 'awaiting_review'}
            className="inline-flex items-center gap-2 px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {unansweredCritical ? (
              <>
                <AlertTriangle className="w-4 h-4" />
                Answer critical questions
              </>
            ) : regenerateRequired ? (
              <>
                <RefreshCw className="w-4 h-4" />
                Regenerate to apply answers
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                {isLastPhase ? 'Approve & finalize' : 'Approve & continue'}
              </>
            )}
          </button>
        )}
      </div>
      <p className="text-xs text-bw-muted text-right -mt-2">
        Regenerate re-runs the full step; “Refine with feedback” records a global correction and
        re-runs only this step; inline edits above are saved per sub-section.
      </p>
      {phaseId === 'pricing-compliance' && (
        <p className="text-xs text-bw-muted text-right -mt-3">
          Note: text corrections steer the narrative, not the priced CLIN/vehicle structure (that
          is computed from the structured solicitation profile).
        </p>
      )}
    </div>
  );
};

export default PhaseReviewPanel;
